import axios from 'axios';
import { exception } from '../utils/analytics';

// APIのベースURL設定
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// axiosインスタンスの作成
const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
});

// キャッシュ機能
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5分

// キャッシュヘルパー関数
const getCacheKey = (url, params = {}) => {
    return `${url}?${JSON.stringify(params)}`;
};

const getCachedData = (key) => {
    const cached = cache.get(key);
    if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
        return cached.data;
    }
    cache.delete(key);
    return null;
};

const setCachedData = (key, data) => {
    cache.set(key, {
        data,
        timestamp: Date.now()
    });
};

// 進行中のリクエストを追跡
const pendingRequests = new Map();

// 重複リクエスト防止機能付きのGET関数
const cachedGet = async (url, config = {}) => {
    const cacheKey = getCacheKey(url, config.params);

    // キャッシュから取得を試行
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
        return { data: cachedData };
    }

    // 進行中のリクエストがある場合は待機
    if (pendingRequests.has(cacheKey)) {
        return pendingRequests.get(cacheKey);
    }

    // 新しいリクエストを作成
    const requestPromise = api.get(url, config).then(response => {
        setCachedData(cacheKey, response.data);
        pendingRequests.delete(cacheKey);
        return response;
    }).catch(error => {
        pendingRequests.delete(cacheKey);
        throw error;
    });

    pendingRequests.set(cacheKey, requestPromise);
    return requestPromise;
};

// リクエストインターセプター
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// NotificationContextを取得するための関数
let notificationHandler = null;
export const setNotificationHandler = (handler) => {
    notificationHandler = handler;
};

// レスポンスインターセプター
api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            sessionStorage.removeItem('token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// キャッシュクリア関数
export const clearCache = () => {
    cache.clear();
    pendingRequests.clear();
};

// 特定のキーのキャッシュを削除
export const clearCacheByPattern = (pattern) => {
    for (const key of cache.keys()) {
        if (key.includes(pattern)) {
            cache.delete(key);
        }
    }
};

// ユーザー関連API（キャッシュ機能付き）
export const getUser = async (userId) => {
    const response = await cachedGet(`/users/${userId}`);
    return response.data;
};

export const getUserRooms = async (userId) => {
    const response = await cachedGet(`/rooms/user/${userId}`);
    return response.data;
};

export const getBlockedUsers = async () => {
    const response = await cachedGet('/users/blocked/list');
    return response.data;
};

// 物件関連API（キャッシュ機能付き）
export const getRoom = async (id) => {
    const response = await cachedGet(`/rooms/${id}`);
    return response.data;
};

// プロフィール関連
export const updateProfile = async (profileData) => {
    const response = await api.put('/users/profile', profileData);
    return response.data;
};

export const getProfile = async () => {
    const response = await api.get('/users/profile');
    return response.data;
};

// メッセージ関連
export const getConversations = async () => {
    const response = await api.get('/messages/conversations');
    return response.data;
};

export const sendMessage = async (conversationId, content) => {
    const response = await api.post(`/messages/${conversationId}`, { content });
    return response.data;
};

export const markAsRead = async (conversationId) => {
    const response = await api.post(`/messages/${conversationId}/read`);
    return response.data;
};

// ユーザー関連
export const register = async (userData) => {
    const response = await api.post('/users/register', userData);
    return response.data;
};

export const login = async (credentials) => {
    const response = await api.post('/users/login', credentials);
    return response.data;
};

// お気に入り関連
export const getFavorites = async () => {
    const response = await api.get('/users/favorites');
    return response.data;
};

export const toggleFavoriteRoom = async (roomId) => {
    const response = await api.post(`/users/favorites/rooms/${roomId}`);
    return response.data;
};

export const toggleFavoriteUser = async (userId) => {
    try {
        const response = await api.post(`/users/favorites/users/${userId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

// Room関連のAPI
export const getRooms = async (page = 1, limit = 12, filters = {}) => {
    const params = new URLSearchParams({
        page,
        limit,
        ...filters
    });
    const response = await api.get(`/rooms?${params}`);
    return response.data;
};

export const createRoom = async (roomData) => {
    const response = await api.post('/rooms', roomData);
    return response.data;
};

export const updateRoom = async (id, data) => {
    const response = await api.put(`/rooms/${id}`, data);
    return response.data;
};

export const deleteRoom = async (id) => {
    const response = await api.delete(`/rooms/${id}`);
    return response.data;
};

export const getNearbyRooms = async (lat, lng, radius) => {
    const response = await api.get('/rooms/map/nearby', {
        params: { lat, lng, radius }
    });
    return response.data;
};

export const getRecommendedRooms = async () => {
    const response = await api.get('/rooms/recommended');
    return response.data;
};

// ユーザーの登録物件一覧を取得
export const getUserListings = async (page = 1, limit = 12) => {
    const response = await api.get('/rooms/user/listings', {
        params: { page, limit }
    });
    return response.data;
};

// 画像アップロード関連
export const uploadRoomImages = async (files) => {
    const formData = new FormData();
    files.forEach((file) => {
        formData.append('images', file);
    });
    const response = await api.post('/rooms/upload-images', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const updateRoomStatus = async (roomId, status) => {
    try {
        const response = await api.patch(`/rooms/${roomId}/status`, { status });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getUserActiveRoomCount = async () => {
    const response = await api.get('/rooms/user/active-count');
    return response.data;
};

// アカウント削除
export const deleteAccount = async () => {
    const response = await api.delete('/users/account');
    return response.data;
};

export { api };  // apiインスタンスも直接エクスポート
export default api; 